import React from "react";
import {
  IDataTableV2DateSearchFilterProps,
  IDatatableV2AdvancedSearchFilter,
} from "./SearchFilter.types";
import { generateDataTableV2ColDefs } from "../../helpers/generateDataTableV2ColDefs";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  SelectChangeEvent,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Assignment,
  CheckBox,
  CheckBoxOutlineBlank,
  Close,
  DeleteForever,
  DeleteOutline,
  FilterList,
  RestartAlt,
  Search,
} from "@mui/icons-material";
import {
  ControlledCustomSelect,
  CustomCurrencyFieldV3,
  CustomNumberField,
  CustomSwitch,
  CustomTextField,
} from "../../../formsComponents";
import produce from "immer";
import { isEqual } from "lodash";
import { DataTableV2FilterButton } from "../../components/DataTableV2FilterButton/DataTableV2FilterButton";
import { useFilterOperation } from "./SearchFilter.hooks";
import { getFilterFieldType } from "./SearchFilter.helpers";
import { CustomDatePicker } from "../../../formsComponents/CustomDatePicker";
import { MuiNumberField } from "../../../formsComponents/MuiNumberField";
import { PhoneTextField } from "../../../PhoneTextField/PhoneTextField";

const operatorsList = [
  { label: "Equal", value: "EQUAL" },
  { label: "Not Equal", value: "NOT_EQUAL" },
  { label: "Greater", value: "GREATER" },
  { label: "Lesser", value: "LESSER" },
  { label: "Greater than equal", value: "GREATER_THAN_EQUAL" },
  { label: "Less than equal", value: "LESSER_THAN_EQUAL" },
  { label: "Contains", value: "CONTAINS" },
  { label: "Starts with", value: "STARTS_WITH" },
  { label: "Ends with", value: "ENDS_WITH" },
  { label: "Is Null", value: "IS_NULL" },
  { label: "Is Not Null", value: "IS_NOT_NULL" },
];

// Operators that don't require a value input.
const NO_VALUE_OPERATORS = ["IS_NULL", "IS_NOT_NULL"];

// const filterConditions = (data: IDatatableV2AdvancedSearchFilter) => {
//   return data.filter((x) => x.value !== "" && x.operator);
// };

export const DataTableV2SearchFilter: React.FC<
  IDataTableV2DateSearchFilterProps
> = (props) => {
  const {
    state,
    masterColumns,
    onChange = () => {},
    excludeColumnsInSearch = [],
    additionalColumns = [],
    loadInitialFilterOncePopoverOpened,
    showMyRecordsButton,
    tableName,
  } = props;
  const theme = useTheme();
  const { advanceConfiguration, loading, error, refetch } = useFilterOperation(
    masterColumns,
    tableName,
  );

  const [searchFilterState, setSearchFilterState] = React.useState(state || []);

  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const canBeOpen = open && Boolean(anchorEl);
  const [columns, setColumns] = React.useState<any>([]);
  const isShowAllChecked =
    showMyRecordsButton &&
    state.length > 0 &&
    state[0].column.includes(showMyRecordsButton.columnName);

  const id = canBeOpen ? "data-table-v2-search-transition-popper" : undefined;

  const executeSearch = (
    newState: IDatatableV2AdvancedSearchFilter,
    prevntDialogToggle?: boolean,
  ) => {
    if (!isEqual(state, newState)) {
      onChange(newState);
      if (!prevntDialogToggle) {
        setOpen((previousOpen) => !previousOpen);
      }
    }
  };

  const handleSearch = () => {
    executeSearch(searchFilterState);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    const newOpen = !open;
    if (newOpen) {
      let initialColumns: IDatatableV2AdvancedSearchFilter = [];
      if (loadInitialFilterOncePopoverOpened && state.length === 0) {
        initialColumns = loadInitialFilterOncePopoverOpened;
      } else {
        initialColumns = [
          {
            column: [],
            value: "",
            logicalOperator: "OR",
            operator: "CONTAINS",
          },
        ];
      }
      setSearchFilterState(state.length > 0 ? state : initialColumns);
    } else {
      setSearchFilterState([]);
    }
    setOpen((previousOpen) => !previousOpen);
  };

  // const handleTextChange =  (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
  //     const value = e.target.value;
  //     const newState = produce(searchFilterState, (draftState) => {
  //       draftState[index].value = value;
  //     });

  //     setSearchFilterState(newState);

  //     // onChange(filterConditions(newState));
  //   };

  const handleTextChange = (index: number) => (value: string) => {
    const newState = produce(searchFilterState, (draftState) => {
      draftState[index].value = value;
    });

    setSearchFilterState(newState);
  };

  const handleColumnSelect = (index: number) => (columns: string[]) => {
    const newState = produce(searchFilterState, (draftState) => {
      draftState[index].column = columns;
      draftState[index].value = NO_VALUE_OPERATORS.includes(
        draftState[index].operator,
      )
        ? null
        : "";
    });
    setSearchFilterState(newState);
  };

  const handleOeprator = (index: number) => (e: SelectChangeEvent<unknown>) => {
    const operator = e.target.value as string;
    const newState = produce(searchFilterState, (draftState) => {
      draftState[index].operator = operator;
      // IS_NULL / IS_NOT_NULL don't take a value — keep it null in the payload.
      if (NO_VALUE_OPERATORS.includes(operator)) {
        draftState[index].value = null;
      }
    });

    setSearchFilterState(newState);

    //executeSearch(filterConditions(newState));
  };

  const handleAdd = () => {
    const newState = produce(searchFilterState, (draftState) => {
      draftState.push({
        column: [],
        value: "",
        logicalOperator: "AND",
        operator: "CONTAINS",
      });
    });
    setSearchFilterState(newState);
  };

  const handleReset = () => {
    setSearchFilterState([
      {
        column: [],
        value: "",
        logicalOperator: "OR",
        operator: "CONTAINS",
      },
    ]);
  };

  const handleRemove = (index: number) => () => {
    if (index < 1) {
      setSearchFilterState([]);
      onChange([]);
      setOpen(false);
    } else {
      const finaldata = [...searchFilterState];
      finaldata.splice(index, 1);
      setSearchFilterState(finaldata);

      // executeSearch(filterConditions(finaldata));
    }
  };

  const handleShowMyRecords = () => {
    if (showMyRecordsButton) {
      if (isShowAllChecked) {
        let initialColumns: IDatatableV2AdvancedSearchFilter = [];

        initialColumns = [
          {
            column: [],
            value: "",
            logicalOperator: "OR",
            operator: "CONTAINS",
          },
        ];

        executeSearch([], true);

        return;
      }
      executeSearch(
        [
          ...state,
          {
            column: [showMyRecordsButton.columnName],
            operator: "EQUAL",
            logicalOperator: "AND",
            value: showMyRecordsButton.currentUserId,
          },
        ],
        true,
      );
    }
  };

  React.useEffect(() => {
    let columnsList = [
      ...generateDataTableV2ColDefs(masterColumns, excludeColumnsInSearch),
    ];
    for (const column of additionalColumns) {
      if (column.placementIndex !== undefined) {
        columnsList.splice(column.placementIndex, 0, column);
      } else {
        columnsList.push(column);
      }
    }
    setColumns(columnsList);
  }, [masterColumns]);
  const isMutpileFilters = searchFilterState.length > 1;

  return (
    <Badge badgeContent={state.length} color="primary">
      <Button
        variant="text"
        aria-describedby={id}
        onClick={handleClick}
        sx={{
          padding: "0px 0px",
          [theme.breakpoints.up("md")]: {
            padding: "2px 2px",
          },
          [theme.breakpoints.up("lg")]: {
            padding: "2px 4px",
          },
          [theme.breakpoints.up("xl")]: {
            padding: "6px 8px",
          },
        }}
      >
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          <FilterList
            color="primary"
            sx={{
              fontSize: 16,
              [theme.breakpoints.up("md")]: {
                fontSize: 18,
              },
              [theme.breakpoints.up("lg")]: {
                fontSize: 23,
              },
              [theme.breakpoints.up("xl")]: {
                fontSize: 25,
              },
            }}
          />
          <Typography
            variant="body1"
            fontSize={"1.2rem"}
            fontWeight={600}
            color="primary.main"
          >
            Advance Search
          </Typography>
        </Stack>
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        sx={{ zIndex: 1200 }}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        marginThreshold={16}
        PaperProps={{
          sx: {
            borderRadius: 3,
            mt: 1,
            boxShadow: "0 12px 40px rgba(0,0,0,0.16)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minWidth: {
              xs: "95vw",
              sm: 650,
              md: 750,
              lg: 850,
              xl: 950,
            },
          },
        }}
      >
        <>
            {/* ---------- Header ---------- */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                py: 2,
                bgcolor: "primary.main",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.15)",
                  }}
                >
                  <FilterList sx={{ fontSize: 22, color: "#fff" }} />
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: "#fff", lineHeight: 1.2 }}
                  >
                    Advanced Search
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.75)", mt: 0.25 }}
                  >
                    Customize your search to find the right records.
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={() => setOpen(false)}
                sx={{
                  border: "1px solid rgba(255,255,255,0.35)",
                  borderRadius: 2,
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                px: 3,
                pt: 1.5,
                pb: 1,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 3,
                  backgroundColor: "rgba(0,0,0,0.2)",
                },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0,0,0,0.2) transparent",
              }}
            >
              {/* ---------- Conditions meta row ---------- */}
              {!isShowAllChecked && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                  >
                    Conditions ·{" "}
                    <Box
                      component="span"
                      sx={{ color: "primary.main", fontWeight: 700 }}
                    >
                      {state.length} active
                    </Box>
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                  >
                    {searchFilterState.length}{" "}
                    {searchFilterState.length === 1 ? "condition" : "conditions"}
                  </Typography>
                </Stack>
              )}
            {showMyRecordsButton && (
              <>
                <Stack direction={"row"} spacing={0} alignItems={"center"}>
                  <CustomSwitch
                    checked={isShowAllChecked}
                    onClick={handleShowMyRecords}
                  />
                  <Typography variant="h4" color="primary" fontWeight={600}>
                    Show My Records
                  </Typography>
                </Stack>
                {isShowAllChecked && (
                  <Stack direction={"row"} justifyContent={"center"}>
                    <Typography
                      variant="body1"
                      fontSize={"1rem"}
                      sx={{
                        color: theme.palette.grey[600],
                      }}
                      width={"85%"}
                      textAlign="center"
                      fontWeight={500}
                    >
                      Currently displaying only your records. To apply
                      additional filters, turn off 'Show My Records'
                    </Typography>
                  </Stack>
                )}
              </>
            )}
            {!isShowAllChecked && (
              <>
                {/* ---------- Shared column headers ---------- */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ display: { xs: "none", sm: "flex" }, mb: 0.5 }}
                >
                  {isMutpileFilters && <Box sx={{ width: 44, minWidth: 44 }} />}
                  <Box sx={{ flex: "0 0 33.33%" }}>
                    <FieldHeader>Column</FieldHeader>
                  </Box>
                  <Box sx={{ flex: "0 0 26.66%" }}>
                    <FieldHeader>Operator</FieldHeader>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FieldHeader>Value</FieldHeader>
                  </Box>
                  {/* spacer matching the row's remove IconButton */}
                  <Box sx={{ width: 40, minWidth: 40 }} />
                </Stack>

                {/* ---------- Filter rows ---------- */}
                <Stack spacing={0.75} sx={{ pt: 0.5 }}>
                  {searchFilterState.map((data, index) => {
                    const column = advanceConfiguration?.columns.find(
                      (opt) => opt.value === data.column[0],
                    );
                    const columnType = getFilterFieldType(column?.type);
                    const hideValue = NO_VALUE_OPERATORS.includes(
                      data.operator,
                    );
                    const filteredColumns =
                      advanceConfiguration?.columns?.filter(
                        (col) => !excludeColumnsInSearch.includes(col.value),
                      ) || [];

                    return (
                      <Stack
                        key={index}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ minHeight: 40 }}
                      >
                        {/* AND / (spacer) badge — only when multiple filters */}
                        {isMutpileFilters &&
                          (index > 0 ? (
                            <Box
                              sx={{
                                width: 44,
                                minWidth: 44,
                                height: 34,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 1.5,
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                letterSpacing: "0.05em",
                              }}
                            >
                              {data.logicalOperator}
                            </Box>
                          ) : (
                            <Box sx={{ width: 44, minWidth: 44 }} />
                          ))}

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.5}
                          alignItems="center"
                          sx={{
                            flex: 1,
                            "& .MuiOutlinedInput-root": { minHeight: 40 },
                          }}
                        >
                          <Box sx={{ flex: { xs: "1", sm: "0 0 33.33%" } }}>
                            <Autocomplete
                              multiple
                              fullWidth
                              disableCloseOnSelect
                              size="small"
                              options={filteredColumns as any}
                              value={
                                (filteredColumns as any[]).filter((opt) =>
                                  data.column.includes(opt.value),
                                )
                              }
                              getOptionLabel={(opt: any) => opt?.label ?? ""}
                              isOptionEqualToValue={(a: any, b: any) =>
                                a.value === b.value
                              }
                              onChange={(_e, selected: any[]) =>
                                handleColumnSelect(index)(
                                  selected.map((s) => s.value),
                                )
                              }
                              limitTags={1}
                              renderOption={(optProps, option: any, { selected }) => (
                                <li {...optProps} key={option.value}>
                                  <Checkbox
                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                    checkedIcon={<CheckBox fontSize="small" />}
                                    checked={selected}
                                    sx={{ mr: 1, p: 0.5 }}
                                  />
                                  {option.label}
                                </li>
                              )}
                              renderTags={(value: any[], getTagProps) =>
                                value.map((option, i) => (
                                  <Chip
                                    {...getTagProps({ index: i })}
                                    key={option.value}
                                    size="small"
                                    label={option.label}
                                  />
                                ))
                              }
                              sx={{
                                ".MuiOutlinedInput-root": {
                                  minHeight: 40,
                                  flexWrap: "nowrap",
                                  overflow: "hidden",
                                  py: "1px",
                                  fontSize: "0.8rem",
                                },
                                ".MuiAutocomplete-input": {
                                  minWidth: "30px !important",
                                },
                              }}
                              renderInput={(params) => (
                                <CustomTextField
                                  {...params}
                                  placeholder={
                                    data.column.length === 0
                                      ? "Select Column"
                                      : ""
                                  }
                                />
                              )}
                            />
                          </Box>
                          <Box sx={{ flex: { xs: "1", sm: "0 0 26.66%" } }}>
                            <ControlledCustomSelect
                              fullWidth
                              value={data.operator}
                              options={operatorsList}
                              onChange={handleOeprator(index)}
                              placeholder="Select Operator"
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            {hideValue ? (
                              <Box
                                sx={{
                                  height: 40,
                                  display: "flex",
                                  alignItems: "center",
                                  px: 1.5,
                                  borderRadius: 1,
                                  border: (t) =>
                                    `1px dashed ${t.palette.divider}`,
                                  color: "text.disabled",
                                  fontSize: "0.8rem",
                                  fontStyle: "italic",
                                }}
                              >
                                No value needed
                              </Box>
                            ) : (
                              <RenderFilterInputWithMemo
                                columnType={columnType}
                                value={data.value}
                                columnOptions={
                                  advanceConfiguration?.columnOptions &&
                                  column?.value
                                    ? advanceConfiguration?.columnOptions[
                                        column?.value as any
                                      ]
                                    : []
                                }
                                handleFilterChange={handleTextChange(index)}
                              />
                            )}
                          </Box>
                        </Stack>

                        <Tooltip title="Remove this condition">
                          <IconButton
                            onClick={handleRemove(index)}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              color: "error.main",
                              border: (t) => `1px solid ${t.palette.error.main}`,
                              "&:hover": {
                                color: "error.main",
                                borderColor: "error.main",
                                bgcolor: (t) => t.palette.error.light + "22",
                              },
                            }}
                          >
                            <DeleteOutline sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    );
                  })}
                </Stack>

                {/* ---------- Add filter condition ---------- */}
                <Box sx={{ mt: 2 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    onClick={handleAdd}
                    sx={{
                      display: "inline-flex",
                      cursor: "pointer",
                      color: "primary.main",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    <Add sx={{ fontSize: 20 }} />
                    <Typography variant="body1" fontWeight={600}>
                      Add filter condition
                    </Typography>
                  </Stack>
                </Box>
              </>
            )}
            </Box>

            {/* ---------- Footer ---------- */}
            {!isShowAllChecked && (
              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  bgcolor: (t) => t.palette.action.hover,
                  borderTop: (t) => `1px solid ${t.palette.divider}`,
                  flexShrink: 0,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  spacing={1.5}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      variant="text"
                      color="inherit"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                      startIcon={<DeleteOutline fontSize="small" />}
                      onClick={handleRemove(-1)}
                    >
                      Remove all
                    </Button>
                    <Button
                      variant="text"
                      color="inherit"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                      startIcon={<RestartAlt fontSize="small" />}
                      onClick={handleReset}
                    >
                      Reset
                    </Button>
                  </Stack>

                  <Button
                    variant="contained"
                    disableElevation
                    sx={{
                      fontWeight: 700,
                      px: 4,
                      py: 1,
                      borderRadius: 2,
                    }}
                    startIcon={<Search fontSize="small" />}
                    onClick={handleSearch}
                  >
                    Search
                  </Button>
                </Stack>
              </Box>
            )}
        </>
      </Popover>
    </Badge>
  );
};

const FieldHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "text.secondary",
    }}
  >
    {children}
  </Typography>
);

interface RenderFilterInputProps {
  value: string;
  columnType: string;
  handleFilterChange: (value: string) => void;
  columnOptions: {
    value: string;
    label: string;
  }[];
}

const RenderFilterInput: React.FC<RenderFilterInputProps> = ({
  value,
  handleFilterChange,
  columnType,
  columnOptions,
}) => {
  switch (columnType) {
    case "select":
      return (
        <ControlledCustomSelect
          options={columnOptions}
          value={columnOptions.find((opt) => opt.value === value)}
          onChange={(evt) => handleFilterChange(evt.target.value as string)}
          placeholder="Select value"
        />
      );

    case "date":
      return (
        <CustomDatePicker
          // placeholder="Select date"
          value={value}
          onChange={(date) => {
            handleFilterChange(date);
          }}
        />
      );

    case "number":
      return (
        <MuiNumberField
          name="filter-numberr-field"
          label=""
          value={value}
          onChange={(e) => handleFilterChange(e.target.value)}
          placeholder="Enter value"
        />
      );

    case "phone":
      return (
        <PhoneTextField
          label=""
          value={value}
          onChange={(newValue) => handleFilterChange(newValue)}
        />
      );

    case "currency":
      return (
        <CustomCurrencyFieldV3
          fullWidth
          label=""
          value={value}
          onChange={(newValue) => handleFilterChange(newValue)}
        />
      );

    default:
      return (
        <>
          <CustomTextField
            fullWidth
            value={value}
            placeholder="Enter value"
            onChange={(e) => handleFilterChange(e.target.value)}
          />
        </>
      );
  }
};

const RenderFilterInputWithMemo = React.memo(RenderFilterInput);